import { html, View } from 'rune-ts';
import klass from './DetachedCardCurationView.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { fx, pipe } from '@fxts/core';
import { ProductCardViewData } from '@services/MarppleShop/features/ProductList/type';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';
import { makeMainUrl } from '@services/MarppleShop/shared/util/url.ts';
import { makeEmptyScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { dataStr } from '@services/MarppleShop/shared/util/dataStr.ts';
import { CardProductControllerView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';

type TProps =
  | {
      device: 'PC';
      layout: 'Detail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
      products: ProductCardViewData[];
    }
  | {
      device: 'Mobile';
      layout: 'Detail';
      id: number;
      mobile_thumbnail: string;
      title: string;
      sub_title: string;
      products: ProductCardViewData[];
    }
  | {
      device: 'PC';
      layout: 'Thumbnail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
    }
  | {
      device: 'Mobile';
      layout: 'Thumbnail';
      id: number;
      thumbnail: string;
      title: string;
      sub_title: string;
    };

/**
 * Detach: https://www.figma.com/design/IIaFJhb53ELp9PZ4XMnQWo?node-id=2832-103008&m=dev#1433454924
 *
 * https://www.figma.com/design/IIaFJhb53ELp9PZ4XMnQWo/%ED%81%90%EB%A0%88%EC%9D%B4%EC%85%98?node-id=2270-137767&m=dev
 */
export class DetachedCardCurationView extends View<TProps> {
  protected override template(props: TProps) {
    if (props.device === 'PC' && props.layout === 'Detail') {
      const { sub_title, products, thumbnail, title, id } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      const curation_detail_data_post_message = pipe(curation_detail_url, makeEmptyScreenNavigate, dataStr);

      return html`<div class="${classes(klass.card_curation, klass.property1_pc)}">
        <a
          class="${klass.thumbnail_wrapper}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <img
            alt=""
            src="${getResizedUrl({
              url: thumbnail,
              format: 'webp',
            })}"
            class="${classes(klass.thumbnail, klass.property1_pc)}"
          />
        </a>

        <div class="${classes(klass.preview, klass.property1_pc)}">
          <a
            class="${classes(klass.title, klass.property1_pc)}"
            href="${curation_detail_url}"
            data-post-message="${curation_detail_data_post_message}"
          >
            <p class="${classes(klass.title_text, klass.property1_pc, font('18_semibold'))}">${title}</p>

            <p class="${classes(klass.sub_title_text, klass.property1_pc, font('12_medium'))}">
              ${sub_title}
            </p>
          </a>

          <div class="${classes(klass.product, klass.property1_pc)}">
            ${fx(products)
              .take(3)
              .map(
                (product) =>
                  new CardProductControllerView({
                    card_product_view_props: {
                      type: 'A',
                      size: 'Xsmall',
                      layout: 'Vertical',
                      theme: 'Light',
                    },
                    product_card_view_data: product,
                    options: {
                      resize_width: 126 * 2,
                      is_lazy: true,
                    },
                  }),
              )
              .toArray()}
          </div>
        </div>
      </div>`;
    } else if (props.device === 'Mobile' && props.layout === 'Detail') {
      const { id, sub_title, title, mobile_thumbnail, products } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      const curation_detail_data_post_message = pipe(curation_detail_url, makeEmptyScreenNavigate, dataStr);

      return html`<div class="${classes(klass.card_curation, klass.property1_mobile)}">
        <a
          class="${klass.thumbnail_wrapper}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <img
            alt=""
            src="${getResizedUrl({
              url: mobile_thumbnail,
              format: 'webp',
            })}"
            class="${classes(klass.thumbnail, klass.property1_mobile)}"
          />
        </a>

        <div class="${classes(klass.preview, klass.property1_mobile)}">
          <a
            class="${classes(klass.title, klass.property1_mobile)}"
            href="${curation_detail_url}"
            data-post-message="${curation_detail_data_post_message}"
          >
            <p class="${classes(klass.title_text, klass.property1_mobile, font('15_semibold'))}">
              ${html.preventEscape(title)}
            </p>

            <p class="${classes(klass.sub_title_text, klass.property1_mobile, font('11_medium'))}">
              ${html.preventEscape(sub_title)}
            </p>
          </a>

          <div class="${classes(klass.product, klass.property1_mobile)}">
            ${fx(products)
              .take(3)
              .map(
                (product) =>
                  new CardProductControllerView({
                    card_product_view_props: {
                      type: 'A',
                      size: 'Xsmall',
                      layout: 'Vertical',
                      theme: 'Light',
                    },
                    product_card_view_data: product,
                    options: {
                      resize_width: 126 * 2,
                      is_lazy: true,
                    },
                  }),
              )
              .toArray()}
          </div>
        </div>
      </div>`;
    } else if (props.device === 'PC' && props.layout === 'Thumbnail') {
      const { sub_title, title, thumbnail, id } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      return html`<div class="${classes(klass.card_curation, klass.property1_variant2)}">
        <a class="${klass.thumbnail_wrapper}" href="${curation_detail_url}">
          <img
            alt=""
            src="${thumbnail}"
            class="${classes(klass.creator_view_thumbnail, klass.property1_variant2)}"
          />
        </a>

        <a
          class="${classes(klass.creator_view_title, klass.property1_variant2)}"
          href="${curation_detail_url}"
        >
          <p class="${classes(klass.creator_view_title_text, klass.property1_variant2, font('15_semibold'))}">
            ${html.preventEscape(title)}
          </p>

          <p
            class="${classes(klass.creator_view_sub_title_text, klass.property1_variant2, font('12_medium'))}"
          >
            ${html.preventEscape(sub_title)}
          </p>
        </a>
      </div>`;
    } else if (props.device === 'Mobile' && props.layout === 'Thumbnail') {
      const { sub_title, title, thumbnail, id } = props;

      const curation_detail_url = makeMainUrl(`/@/curations/${id}`);

      const curation_detail_data_post_message = pipe(curation_detail_url, makeEmptyScreenNavigate, dataStr);

      return html`<div class="${classes(klass.card_curation, klass.property1_variant3)}">
        <a
          class="${klass.thumbnail_wrapper}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <img
            alt=""
            src="${thumbnail}"
            class="${classes(klass.creator_view_thumbnail, klass.property1_variant3)}"
          />
        </a>

        <a
          class="${classes(klass.creator_view_title, klass.property1_variant3)}"
          href="${curation_detail_url}"
          data-post-message="${curation_detail_data_post_message}"
        >
          <p class="${classes(klass.creator_view_title_text, klass.property1_variant3, font('14_semibold'))}">
            ${html.preventEscape(title)}
          </p>

          <p
            class="${classes(klass.creator_view_sub_title_text, klass.property1_variant3, font('12_medium'))}"
          >
            ${html.preventEscape(sub_title)}
          </p>
        </a>
      </div>`;
    } else {
      throw new Error();
    }
  }
}
